import { Box, Divider, Grid2 as Grid, Link, styled } from '@mui/material'
import { FontIcon } from '@trr/internal-helpers'
import Logo from 'App/components/Logo'
import { useMenuStyles } from 'App/LoggedInNavigation/LoggedInMenu/useMenuStyles'
import { useDashboard } from 'App/hooks'
import Surfly from 'App/Surfly/Surfly'
import { useCachedContentQuery, useCachedSettingsState } from 'PublicWebApi'

interface WrapperGridProps {
  isFullFunctionPage: boolean
  isSurflyShowing: boolean
}

const WrapperGrid = styled(Grid, {
  shouldForwardProp: (prop: string) =>
    !['isFullFunctionPage', 'isSurflyShowing'].includes(prop),
})<WrapperGridProps>(({ theme, isFullFunctionPage, isSurflyShowing }) => ({
  marginBlock: `${theme.spacing(4)} ${theme.spacing(5)}`,
  marginInline: 'auto',
  maxWidth: isFullFunctionPage ? '1900px' : '1140px',

  [theme.breakpoints.up('md')]: {
    ...(isSurflyShowing && {
      marginStart: theme.spacing(10),
    }),
  },
}))

const FooterGrid = styled(Grid)(({ theme }) => ({
  alignItems: 'center',
  gap: theme.spacing(2),
  [theme.breakpoints.up('md')]: {
    gap: theme.spacing(3),
  },
}))

const FooterLink = styled(Link)(({ theme }) => ({
  ...theme.typography.subtitle2,
  display: 'flex',
  gap: theme.spacing(1),
  padding: theme.spacing(0.25),
  color: theme.palette.text?.primary,
  whiteSpace: 'nowrap',
  border: 'solid 1px transparent',
  borderRadius: '2px',
  alignItems: 'center',

  '&:hover, &:active': {
    textDecoration: 'underline',
  },

  '&:focus': {
    outline: 'none',
    borderColor: theme.palette.primary?.main,

    '&:not(:focus-visible)': {
      borderColor: theme.palette.primary?.main,
    },
  },
}))

const SurflyWrapper = styled(Box)(({ theme }) => ({
  svg: {
    fill: theme.palette.text.primary,
  },
  button: {
    border: 'solid 1px transparent',
    borderRadius: '42px',
    '&:focus': {
      outline: 'none',
      borderColor: theme.palette.primary?.main,

      '&:not(:focus-visible)': {
        borderColor: theme.palette.primary?.main,
      },
    },
  },
}))

const LoggedInFooter = () => {
  const content = useCachedSettingsState().footerNavigation
  const { data } = useCachedContentQuery()
  const isFullFunctionPage = data?.contentType.at(1) === 'FunctionPageFull'
  const isDashboardPage = useDashboard()
  const { leftMenuLgWidth, leftMenuPaddingRight } = useMenuStyles()

  const surflyFeature = window.FEATURE_FLAGS.includes(
    'Public-Web-Frontend_surfly_perma_240927'
  )

  const marginStyles = isDashboardPage
    ? {
        marginLeft: {
          xs: 0,
          lg: `${leftMenuLgWidth + leftMenuPaddingRight}px`,
        },
      }
    : { marginLeft: 0 }

  const paddingStyles = isDashboardPage
    ? {
        px: { xs: 2, sm: 3, md: 6 },
        pl: { lg: 0 },
      }
    : {
        px: { xs: 2, sm: 3, md: 6 },
      }

  const footerStyles = {
    ...marginStyles,
    ...paddingStyles,
  }

  return (
    <Box data-testid="logged-in-footer" component="footer" sx={footerStyles}>
      <WrapperGrid
        container
        gap={{ xs: 2 }}
        alignItems={'center'}
        justifyContent={'space-between'}
        isFullFunctionPage={isFullFunctionPage}
        isSurflyShowing={surflyFeature}
      >
        <Divider sx={{ width: '100%', marginBottom: 2 }} />

        <FooterGrid container>
          <Grid
            size={{ xs: 12, md: 'auto' }}
            display={'flex'}
            alignItems={'center'}
            paddingRight={2}
          >
            <Logo height={25} data-testid="loggedin-footer-logo" />
          </Grid>
          {content.contactLinksArea.slice(0, 2).map((link) => (
            <FooterLink href={link.url} key={link.linkText}>
              <FontIcon iconName={link.iconName} />
              {link.linkText}
            </FooterLink>
          ))}
        </FooterGrid>

        <FooterGrid container>
          {content.legalLinksArea.map((link) => (
            <FooterLink href={link.url} key={link.linkText}>
              {link.linkText}
            </FooterLink>
          ))}
          {surflyFeature && (
            <SurflyWrapper>
              <Surfly />
            </SurflyWrapper>
          )}
        </FooterGrid>
      </WrapperGrid>
    </Box>
  )
}

export default LoggedInFooter
